import { useSelector } from 'react-redux'
import { NonInteractiveTooltip } from '../custom'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { OiDataTypeBars } from '../../pages/home/plChart'
import { selectPrimarySecurity } from '../../selectors/home'
import { getSecurityKey, isFnOSecurity } from '../../util/security'
import { CalculateMarginResponse, TransactionType, Broker_Variety, OrderType } from '../../proto/generated/order'
import { getMargin } from '../../services/apis/misc'
import { CalculatePopFunction, CalculateProfit, generatePlChartData, getCurentOrderPricesFromData, ifFiniteNumber, updateDataWithBreakevenPoints } from '../../util/tradepanel'
import { selectUnderlyingOptionsTradeData } from '../../selectors/options'
import { selectSnapshot } from '../../selectors/security'
import { CircularProgress, Grid, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { formatLargeNumber } from '../../util/screener'
import { selectOrdersData } from '../../selectors/orders'
import { useCombineBasketAnalyzeAndLegOrders } from '../../hooks/useCombineBasketAnalyzeAndLegOrders'
import { OrderDataPrices, OrdersDataType } from '../../reducer/orders.reducer'
import { OptionChainViewKey } from '../../assets/constants'
import { selectSimulatorDate } from '../../selectors/simulator'
import { selectPrimaryTradingAccount } from '../../selectors/user'
import InfoIcon from '../../assets/icons/InfoIcon'

const formatBreakevenValues = (breakeven: number[]): string => {
    return breakeven.reduce((prev: string, curr: number, ind: number) => {
        const formattedValue = curr.toFixed(0)
        if (ind < breakeven.length - 1) {
            return prev + `${formattedValue}, `
        }
        return prev + formattedValue
    }, '')
}

const formatPopPercentage = (pop: number): string => {
    return `${pop.toFixed(0)}%`
}

const formatMarginTooltip = (margin: number): string => {
    return Number(margin.toFixed(2)).toLocaleString('en-IN', { style: 'decimal' })
}
import { selectTradeOrders } from '../../selectors/strategyBuilder'
import { TradeOrder } from '../../reducer/strategyBuilder.reducer'
import commonActions from '../../actions/common.actions'
import { useDispatch } from 'react-redux'

export type StrategyInfoProps = {
    simulator: boolean
}

const StrategyInfo: React.FC<StrategyInfoProps> = ({ simulator }) => {
    const optionChainViewKey = simulator ? OptionChainViewKey.HISTORICAL : OptionChainViewKey.LIVE
    const orderData = useSelector(selectOrdersData)
    const primaryTradingAccount = useSelector(selectPrimaryTradingAccount)
    const simulatorTimestamp = useSelector(selectSimulatorDate)
    const tradeOrders = useSelector(selectTradeOrders);
    const { orderState, basketAnalyzeDetails, product, orderType } = orderData
    const { orders = [], ordersPrices = [], parentSquareOffOrders = [] } = useCombineBasketAnalyzeAndLegOrders()
    const [dataObj, setDataObj] = useState<{
        pop: undefined | number
        ext: undefined | number
        p50: undefined | number
        delta: undefined | number
        theta: undefined | number
        maxProfit: undefined | string
        maxLoss: undefined | string
        breakeven: number[]
    }>({
        pop: undefined,
        ext: undefined,
        p50: undefined,
        delta: undefined,
        theta: undefined,
        maxProfit: undefined,
        maxLoss: undefined,
        breakeven: []
    })
    const dispatch = useDispatch()
    const [profitData, setProfitData] = useState<OiDataTypeBars[]>([])
    const { expressionMode, primary, underlying } = useSelector(selectPrimarySecurity)
    const mainSecurity = expressionMode ? null : primary && isFnOSecurity(primary) ? underlying : primary
    const underlyingToken = mainSecurity ? getSecurityKey(mainSecurity.exchange, mainSecurity.code) : ''
    const optionExpiries = useSelector(selectUnderlyingOptionsTradeData(underlyingToken))
    const mainSecuritySnapshot = useSelector(selectSnapshot(underlyingToken))
    const [fetchedMargin, setFetchedMargin] = useState<undefined | number | string>(undefined)
    const profitsDataCreated = useRef<boolean>(false)
    const marginReqString = useRef('')


    const [ordersForCalculation,orderPricesForCalculation] = useMemo(() => {
        // if (!simulator) {
        //     return tradeOrders;
        // }
        const newOrders: OrdersDataType[] = []
        const newOrderPrices: OrderDataPrices[] = []
        orders.forEach((order, i) => {
            if ((order.timeStamp ?? 0) <= simulatorTimestamp) {
                newOrders.push(order)
                newOrderPrices.push(ordersPrices[i])
            }
        })
        return [newOrders, newOrderPrices]
    }, [simulator, tradeOrders, simulatorTimestamp])

    const calculateMargin = useCallback(
        (passedOrdersPrices = orderPricesForCalculation, primaryAccountChanged : boolean = false) => {
            if(!primaryTradingAccount) {
                commonActions.setApiError(dispatch, {
                    error: true,
                    message: "Connect a trading account to place trades",
                    errorType: 'info'
                })
                return
            }
            // fetching margin
            if (optionChainViewKey === OptionChainViewKey.LIVE) {
                const data: any = ordersForCalculation.filter(o=>!o?.readonly).map((order, i) => ({
                    exchange: order.security?.exchange,
                    security: {
                        exchange: order.security.exchange,
                        code: order.security.code
                    },
                    position: order.quantity * (order.security?.lotSize ?? 0),
                    transactionType: order.type === 'buy' ? TransactionType.BUY : TransactionType.SELL,
                    price: Number((Number(passedOrdersPrices[i]?.price) * (mainSecurity?.priceMultiplier ?? 100)).toFixed(0)),
                    strike_price: order.security.strikePrice,
                    expiry: order.security.expiry.expiry,
                    optionType: order.security.optionType,
                    orderType: orderType,
                    product: product,
                    variety: Broker_Variety.REGULAR
                }))
                if (JSON.stringify(data) !== marginReqString.current || primaryAccountChanged) {
                    setFetchedMargin(undefined)
                    marginReqString.current = JSON.stringify(data)
                    getMargin({
                        body: {
                            orders: data,
                            tradingAccount: {
                                accountId : primaryTradingAccount.accountId,
                                brokerId : primaryTradingAccount.brokerCode
                            }
                        }
                    })
                        .then((res: CalculateMarginResponse) => {
                            setFetchedMargin(res.totalMargin)
                        })
                        .catch((e) => {
                            setFetchedMargin('-')
                        })
                }
            } else {
                setFetchedMargin('-')
            }
        },
        [orderPricesForCalculation, optionChainViewKey, ordersForCalculation, mainSecurity?.priceMultiplier , product, orderType, primaryTradingAccount]
    )

    useEffect(()=>{
        calculateMargin(orderPricesForCalculation, true)
    },[primaryTradingAccount])

    const calculateDataObj = useCallback(
        async (newData: OiDataTypeBars[]) => {
            let delta = 0
            let theta = 0
            let maxProfit = orders.some((o) => o.contractType === 'FUTURE') ? Infinity : 0
            let maxLoss = orders.some((o) => o.contractType === 'FUTURE') ? -Infinity : 0
            let maxProfitInfinity = false
            let maxLossInfinity = false
            calculateMargin()

            const isCompleteProfitZero = newData.every((d) => d.profit === 0 && d.strike !== 0)
            if (newData.length === 0 || isCompleteProfitZero) {
                setDataObj({
                    pop: 0,
                    ext: 0,
                    p50: 0,
                    delta: 0,
                    theta: 0,
                    maxProfit: '-',
                    maxLoss: '-',
                    breakeven: []
                })
                return
            }
            newData.forEach((d) => {
                if (d?.profit > maxProfit) {
                    maxProfit = d?.profit
                }
                if (d?.profit < maxLoss) {
                    maxLoss = d?.profit
                }
            })
            const strikes = orders.map((o, i) => (o.contractType === 'FUTURE' ? Number(ordersPrices[i]?.price ?? 0) : o.data?.strikeCol?.strike / (mainSecurity?.priceMultiplier ?? 100))).sort()
            if (strikes.length) {
                const lowestStrikeOrder = newData.findIndex((d) => d.strike === strikes[0])
                const highestStrikeOrder = newData.findIndex((d) => d.strike === strikes[strikes.length - 1])
                if (newData[lowestStrikeOrder]?.profit > newData[lowestStrikeOrder - 1]?.profit || newData[highestStrikeOrder]?.profit > newData[highestStrikeOrder + 1]?.profit) {
                    maxLossInfinity = true
                }
                if (newData[lowestStrikeOrder]?.profit < newData[lowestStrikeOrder - 1]?.profit || newData[highestStrikeOrder]?.profit < newData[highestStrikeOrder + 1]?.profit) {
                    maxProfitInfinity = true
                }
            }
            if (maxLossInfinity) {
                maxLoss = -Infinity
            }
            if (maxProfitInfinity) {
                maxProfit = Infinity
            }
            orders.forEach((order) => {
                if (order.contractType !== 'OPTION') {
                    delta += order.type === 'buy' ? 1 : -1
                    theta += 0
                    return
                }
                const orderDelta = order.callput === 'call' ? order.data.callOption?.optionGreeks?.delta : order.data.putOption?.optionGreeks?.delta
                const orderTheta = order.callput === 'call' ? order.data.callOption?.optionGreeks?.theta : order.data.putOption?.optionGreeks?.theta
                if (order.type === 'buy') {
                    delta += (orderDelta ?? 0) * order.quantity * (order.security?.lotSize ?? 50)
                    theta += (orderTheta ?? 0) * order.quantity * (order.security?.lotSize ?? 50)
                } else {
                    delta -= (orderDelta ?? 0) * order.quantity * (order.security?.lotSize ?? 50)
                    theta -= (orderTheta ?? 0) * order.quantity * (order.security?.lotSize ?? 50)
                }
            })

            const pop = CalculatePopFunction(profitData, optionExpiries ?? [], orders, mainSecurity?.priceMultiplier ?? 100)
            const breakeven = updateDataWithBreakevenPoints(profitData).breakeven

            setDataObj({
                ...dataObj,
                pop,
                delta,
                theta,
                maxLoss: Math.round(Number(maxLoss))?.toLocaleString('en-IN', { style: 'decimal' }),
                maxProfit: Math.round(Number(maxProfit))?.toLocaleString('en-IN', { style: 'decimal' }),
                breakeven
            })
        },
        [calculateMargin, dataObj, mainSecurity?.priceMultiplier, optionExpiries, orders, ordersPrices, profitData]
    )

    useEffect(() => {
        if (((orders && orders.length > 0) || (parentSquareOffOrders && parentSquareOffOrders.length > 0)) && profitData && profitData.length > 0) {
            const parentSquareOffOrdersPrices: OrderDataPrices[] =
                parentSquareOffOrders.map((o) => {
                    return {
                        price: ((o.orderDetails?.price ?? 0) / (o.security?.priceMultiplier ?? 100)).toFixed(2) ?? '',
                        topAsk: 0,
                        topBid: 0,
                        mid: 0,
                        visibility: true
                    }
                }) ?? ([] as OrderDataPrices[])
            mainSecurity &&
                CalculateProfit(
                    profitData,
                    [...ordersForCalculation, ...parentSquareOffOrders],
                    [...orderPricesForCalculation, ...parentSquareOffOrdersPrices],
                    mainSecurity,
                    setProfitData,
                    0,
                    optionChainViewKey === OptionChainViewKey.LIVE ? Date.now() : simulatorTimestamp
                )
        }
    }, [orders, ordersForCalculation, orderPricesForCalculation, mainSecurity, ordersPrices, parentSquareOffOrders, simulatorTimestamp, optionChainViewKey])

    useEffect(() => {
        ;(async () => {
            if (!profitsDataCreated.current && optionExpiries) {
                const newOptionsExpiries = [...optionExpiries]
                let index = Infinity
                if (orders.length > 0) {
                    index = newOptionsExpiries.findIndex((d) => d.expiration.expiry?.expiry === orders[0].security?.expiry?.expiry)
                    orders.forEach((order, i) => {
                        if (i > 0) {
                            const newIndex = newOptionsExpiries.findIndex((d) => d.expiration.expiry?.expiry === orders[i].security?.expiry?.expiry)
                            if (newIndex !== -1 && newIndex < index) {
                                index = newIndex
                            }
                        }
                    })
                }
                if (index === -1 || !isFinite(index)) {
                    index = 0
                }
                const data = newOptionsExpiries.filter((t) => !t.derived)?.[index]?.trades
                if (data && data.length > 0) {
                    const createdProfitData = generatePlChartData({ data, ltp: mainSecuritySnapshot?.lastTradedPrice, priceMultiplier: mainSecurity?.priceMultiplier })
                    setProfitData(createdProfitData)
                    if (createdProfitData && createdProfitData?.length > 0 && optionExpiries) {
                        profitsDataCreated.current = true
                        if (orders && orders.length > 0 && createdProfitData && createdProfitData.length > 0) {
                            const newOrdersPrices = await getCurentOrderPricesFromData(orders, optionExpiries, underlyingToken, [])
                            const parentSquareOffOrdersPrices: OrderDataPrices[] =
                                parentSquareOffOrders.map((o) => {
                                    return {
                                        price: ((o.orderDetails?.price ?? 0) / (o.security?.priceMultiplier ?? 100)).toFixed(2) ?? '',
                                        topAsk: 0,
                                        topBid: 0,
                                        mid: 0,
                                        visibility: true
                                    }
                                }) ?? ([] as OrderDataPrices[])
                            mainSecurity &&
                                CalculateProfit(
                                    createdProfitData,
                                    [...ordersForCalculation, ...parentSquareOffOrders],
                                    [...orderPricesForCalculation, ...parentSquareOffOrdersPrices],
                                    mainSecurity,
                                    setProfitData,
                                    0,
                                    optionChainViewKey === OptionChainViewKey.LIVE ? Date.now() : simulatorTimestamp
                                )
                        }
                    }
                }
            }
        })()
    }, [optionExpiries, orders, ordersForCalculation, underlyingToken, simulatorTimestamp, optionChainViewKey])

    useEffect(() => {
        if (
            ((orders && orders.length > 0) || (basketAnalyzeDetails?.positionOrders && basketAnalyzeDetails.positionOrders.length > 0)) &&
            profitData &&
            profitData.length > 0 &&
            (orderState === 'order' || orderState === 'waiting' || optionChainViewKey === OptionChainViewKey.HISTORICAL)
        ) {
            calculateDataObj(profitData)
        }
    }, [profitData, optionChainViewKey, orders, primaryTradingAccount])

    useEffect(() => {
        if (
            ((orders && orders.length > 0) || (basketAnalyzeDetails?.positionOrders && basketAnalyzeDetails.positionOrders.length > 0)) &&
            (profitData.length === 0 || orderState === 'square-off') &&
            optionChainViewKey !== OptionChainViewKey.HISTORICAL
        ) {
            calculateDataObj([])
        }
    }, [ordersPrices, primaryTradingAccount])

    return (
        <>
            <Stack direction={'row'} sx={{ display: 'flex', gap: 4 }}>
                <Grid item>
                    <Grid item xs={12} textAlign={'center'}>
                        <Typography variant="caption" component="span" color={'text.secondary'} className="trade-panel-text">
                            MAX PROFIT
                        </Typography>
                    </Grid>
                    <Grid item xs={12} textAlign={'center'}>
                        <Typography className="green-text" variant="body1" component="span" color="primaryText" fontWeight={500}>
                             {dataObj.maxProfit ?? <CircularProgress style={{ height: 12, width: 12 }} color="inherit" />}
                        </Typography>
                    </Grid>
                </Grid>
                <Grid item>
                    <Grid item xs={12} textAlign={'center'}>
                        <Typography variant="caption" component="span" color={'text.secondary'} className="trade-panel-text">
                            MAX LOSS
                        </Typography>
                    </Grid>
                    <Grid item xs={12} textAlign={'center'}>
                        <Typography className="red-text" variant="body1" component="span" color="primaryText" fontWeight={500}>
                            {dataObj.maxLoss ?? <CircularProgress style={{ height: 12, width: 12 }} color="inherit" />}
                        </Typography>
                    </Grid>
                </Grid>
                <Grid item>
                    <Grid item xs={12} textAlign={'center'}>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            <Typography variant="caption" component="span" color={'text.secondary'} className="trade-panel-text">
                                MARGIN
                            </Typography>
                            {optionChainViewKey!==OptionChainViewKey.LIVE &&
                                <Tooltip title="Margin calculation is not available in simulator">
                                    <IconButton
                                        size="small"
                                        sx={{ padding: 0 }}
                                    >
                                        <InfoIcon sx={{ fontSize: '0.75rem' }}/>
                                    </IconButton>
                                </Tooltip>
                            }
                        </div>
                    </Grid>
                    <Grid item xs={12} textAlign={'center'}>
                        <NonInteractiveTooltip
                            title={
                                fetchedMargin === '-' || isNaN(Number(Number(fetchedMargin)?.toFixed(2)))
                                    ? null
                                    : formatMarginTooltip(Number(fetchedMargin))
                            }
                            placement="top"
                            arrow
                        >
                            <Typography className="trade-panel-bold-text" variant="body1" component="span" color="primaryText" fontSize={'small'}>
                                {fetchedMargin === '-' ? (
                                    '-'
                                ) : !isNaN(Number(Number(fetchedMargin)?.toFixed(2))) ? (
                                    formatLargeNumber(Number(fetchedMargin))
                                ) : (
                                    <CircularProgress style={{ height: 12, width: 12 }} color="inherit" />
                                )}
                            </Typography>
                        </NonInteractiveTooltip>
                    </Grid>
                </Grid>
                <Grid item>
                    <Grid item xs={12} textAlign={'center'}>
                        <Typography variant="caption" component="span" color={'text.secondary'} className="trade-panel-text">
                            DELTA
                        </Typography>
                    </Grid>
                    <Grid item xs={12} textAlign={'center'}>
                        <NonInteractiveTooltip title={dataObj.delta && dataObj.delta !== 0 ? dataObj.delta?.toFixed(2) : null} placement="top" arrow>
                            <Typography className="trade-panel-bold-text" variant="body1" component="span" color="primaryText" fontSize={'small'}>
                                {dataObj.delta === 0 ? (
                                    '-'
                                ) : dataObj.delta ? (
                                    formatLargeNumber(Number(dataObj.delta.toFixed(2)))
                                ) : (
                                    <CircularProgress style={{ height: 12, width: 12 }} color="inherit" />
                                )}
                            </Typography>
                        </NonInteractiveTooltip>
                    </Grid>
                </Grid>
                <Grid item>
                    <Grid item xs={12} textAlign={'center'}>
                        <Typography variant="caption" component="span" color={'text.secondary'} className="trade-panel-text">
                            THETA
                        </Typography>
                    </Grid>
                    <Grid item xs={12} textAlign={'center'}>
                        <NonInteractiveTooltip title={dataObj.theta && dataObj.theta !== 0 ? dataObj.theta?.toFixed(2) : null} placement="top" arrow>
                            <Typography className="trade-panel-bold-text" variant="body1" component="span" color="primaryText" fontSize={'small'}>
                                {dataObj.theta === 0 ? (
                                    '-'
                                ) : dataObj.theta ? (
                                    formatLargeNumber(Number(dataObj.theta.toFixed(2)))
                                ) : (
                                    <CircularProgress style={{ height: 12, width: 12 }} color="inherit" />
                                )}
                            </Typography>
                        </NonInteractiveTooltip>
                    </Grid>
                </Grid>
                <Grid item>
                    <Grid item xs={12} textAlign={'center'}>
                        <Typography variant="caption" component="span" color={'text.secondary'} className="trade-panel-text">
                            POP
                        </Typography>
                    </Grid>
                    <Grid item xs={12} textAlign={'center'}>
                        <Typography className="trade-panel-bold-text" variant="body1" component="span" color="primaryText" fontSize={'small'}>
                            {dataObj.pop === 0 ? '-' : dataObj.pop ? formatPopPercentage(dataObj.pop) : <CircularProgress style={{ height: 12, width: 12 }} color="inherit" />}
                        </Typography>
                    </Grid>
                </Grid>
                <Grid item>
                    <Grid item xs={12} textAlign={'center'}>
                        <Typography variant="caption" component="span" color={'text.secondary'} className="trade-panel-text">
                            BREAKEVEN
                        </Typography>
                    </Grid>
                    <Grid item xs={12} textAlign={'center'}>
                        <Typography className="trade-panel-bold-text" variant="body1" component="span" color="primaryText" fontSize={'small'}>
                            {!dataObj.breakeven.length ? (
                                '-'
                            ) : dataObj.breakeven ? (
                                formatBreakevenValues(dataObj.breakeven)
                            ) : (
                                <CircularProgress style={{ height: 12, width: 12 }} color="inherit" />
                            )}
                        </Typography>
                    </Grid>
                </Grid>
            </Stack>
        </>
    )
}

export default memo(StrategyInfo)
